import { observer } from "mobx-react-lite";
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Close,
  CheckCircle,
  Error,
  Cancel,
  CloudDownload,
} from "@mui/icons-material";
import { downloadManager, DownloadStatus } from "../../services/download/DownloadManagerService";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function StatusIcon({ status }: { status: DownloadStatus }) {
  switch (status) {
    case "completed": return <CheckCircle sx={{ color: "success.main", fontSize: 20 }} />;
    case "failed": return <Error sx={{ color: "error.main", fontSize: 20 }} />;
    case "cancelled": return <Cancel sx={{ color: "text.disabled", fontSize: 20 }} />;
    default: return <CloudDownload sx={{ color: "primary.main", fontSize: 20 }} />;
  }
}

export default observer(function DownloadPanel() {
  if (!downloadManager.hasDownloads) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        width: 360,
        zIndex: 1300,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "primary.main",
          color: "white",
          px: 2,
          py: 1,
          cursor: "pointer",
        }}
        onClick={() => downloadManager.toggleExpanded()}
      >
        <Typography variant="subtitle2">
          Downloads {downloadManager.activeCount > 0 ? `(${downloadManager.activeCount})` : ""}
        </Typography>
        <Box>
          {downloadManager.activeCount === 0 && (
            <IconButton size="small" sx={{ color: "white" }} onClick={(e) => { e.stopPropagation(); downloadManager.clearCompleted(); }}>
              <Close fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" sx={{ color: "white" }}>
            {downloadManager.isExpanded ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={downloadManager.isExpanded}>
        <List dense sx={{ maxHeight: 300, overflow: "auto", py: 0 }}>
          {downloadManager.downloads.map((item) => (
            <ListItem key={item.id} sx={{ flexDirection: "column", alignItems: "stretch", py: 1, px: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <StatusIcon status={item.status} />
                <ListItemText
                  primary={item.fileName}
                  primaryTypographyProps={{ variant: "body2", noWrap: true }}
                  secondary={
                    item.status === "downloading"
                      ? item.total > 0
                        ? `${formatBytes(item.loaded)} / ${formatBytes(item.total)} — ${item.progress}%`
                        : item.loaded > 0
                          ? formatBytes(item.loaded)
                          : "Preparing..."
                      : item.status === "completed"
                        ? "Completed"
                        : item.status === "cancelled"
                          ? "Cancelled"
                          : "Failed"
                  }
                  secondaryTypographyProps={{ variant: "caption" }}
                />
                <ListItemSecondaryAction>
                  {item.status === "downloading" ? (
                    <IconButton size="small" onClick={() => downloadManager.cancelDownload(item.id)}>
                      <Close fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton size="small" onClick={() => downloadManager.removeDownload(item.id)}>
                      <Close fontSize="small" />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </Box>
              {item.status === "downloading" && (
                <LinearProgress
                  variant={item.total > 0 ? "determinate" : "indeterminate"}
                  value={item.progress}
                  sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                />
              )}
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Paper>
  );
});
